import type { Scale } from '@tunasong/schemas'
import type { Chord, ChordVariant } from '../chord/index.js'
import { isChordVariant } from '../chord/chord-types.js'
import { getNoteChromaticDistance } from '../note/note-lib.js'
import { isDominantSeventhVariant } from './diatonic.js'
import type { ChordBorrowing, ChordFunction } from './types.js'

/**
 * Map from semitones-above-tonic to the secondary-dominant label, in MAJOR keys.
 *
 * The dominant of any chord is a perfect fifth above its target. Mapping
 * the targets we support to their dominants in C major:
 *
 *   target ii (D)  → V/ii is A  → A is 9 semitones above C
 *   target IV (F)  → V/IV is C  → 0 (shares root with tonic — see below)
 *   target V  (G)  → V/V  is D  → 2
 *   target vi (A)  → V/vi is E  → 4
 *
 * V/IV (C7 in C major) shares its root with the tonic. We disambiguate by
 * chord-quality: V/IV must be a dominant-7 chord. We require that across all
 * secondary dominants in v1 (a plain triad is not a "secondary dominant" in
 * the analytical sense — it's just a major chord that happens to be a fifth
 * above a target).
 */
const SEC_DOM_BY_SEMITONES_MAJOR: Record<number, ChordFunction> = {
  9: 'V/ii',
  0: 'V/IV',
  2: 'V/V',
  4: 'V/vi',
}

/**
 * Roman-numeral targets for `V/X` secondary dominants. Used by the analyzer to
 * check whether a follow-up chord (`context.next`) matches the expected
 * resolution. We compare chord-function-to-chord-function (via
 * `getDiatonicFunction`) rather than chord-root-to-chord-root, which means
 * resolutions like `V/V → G7` count (G7's diatonic function is `'V'`).
 */
export const EXPECTED_RESOLUTION_TARGETS = {
  'V/ii': 'ii',
  'V/IV': 'IV',
  'V/V': 'V',
  'V/vi': 'vi',
} as const

export type SecondaryDominantLabel = keyof typeof EXPECTED_RESOLUTION_TARGETS

export const getSecondaryDominantFunction = (
  chord: Chord,
  scale: Scale
): { chordFunction: ChordFunction; borrowing: ChordBorrowing } | null => {
  if (scale.type !== 'major') {
    // v1 supports secondary dominants in major keys only.
    return null
  }
  if (!isDominantSeventh(chord)) {
    return null
  }
  const semitones = getNoteChromaticDistance(scale.root, chord.root)
  const fn = SEC_DOM_BY_SEMITONES_MAJOR[semitones]
  if (!fn) {
    return null
  }
  return { chordFunction: fn, borrowing: 'secondary-dominant' }
}

/**
 * Minor-KEY secondary dominants (Bowie, GH #2147), by semitones above the
 * tonic. In E minor:
 *
 *   target iv   (Am) → V/iv   is E  → 0 (shares the tonic's root, like V/IV)
 *   target V    (B)  → V/V    is F♯ → 2 (GH #2164; resolves to the harmonic-minor V)
 *   target ♭VI  (C)  → V/♭VI  is G  → 3 (GH #2383; dominant-7 family only: the plain G is diatonic ♭III)
 *   target ♭VII (D)  → V/♭VII is A  → 5
 *
 * V/♭III (D7) is the diatonic ♭VII7 and V/v (B7) is the harmonic-minor V, so
 * neither is listed. Kept apart from the major-key detector, which the
 * secondary-dominant suggestion chips and the agent tool rely on staying
 * major-only.
 */
const SEC_DOM_BY_SEMITONES_MINOR: Record<number, ChordFunction> = {
  0: 'V/iv',
  2: 'V/V',
  3: 'V/♭VI',
  5: 'V/♭VII',
}

const RESOLUTION_TARGETS: Partial<Record<ChordFunction, ChordFunction>> = {
  ...EXPECTED_RESOLUTION_TARGETS,
  'V/iv': 'iv',
  'V/♭VI': '♭VI',
  'V/♭VII': '♭VII',
}

/**
 * The chord function a secondary dominant resolves to, in major or minor
 * keys; undefined for any other label.
 */
export const getExpectedResolutionTarget = (chordFunction: ChordFunction): ChordFunction | undefined =>
  RESOLUTION_TARGETS[chordFunction]

/**
 * Secondary dominant in the natural-minor KEY (`scale.type === 'minor'`):
 * E7 → V/iv, F♯7 → V/V and A7 → V/♭VII in E minor. Modes are left alone, as for the
 * harmonic-minor V lift.
 */
export const getMinorKeySecondaryDominantFunction = (
  chord: Chord,
  scale: Scale
): { chordFunction: ChordFunction; borrowing: ChordBorrowing } | null => {
  if (scale.type !== 'minor' || !isDominantSeventh(chord)) {
    return null
  }
  const fn = SEC_DOM_BY_SEMITONES_MINOR[getNoteChromaticDistance(scale.root, chord.root)]
  return fn ? { chordFunction: fn, borrowing: 'secondary-dominant' } : null
}

/**
 * Is the chord in the dominant-seventh family (C7, C9, C7♭9, C7♯9, C7♯11,
 * C7♭13, C7♯5, C7♭5)? All share the major third and ♭7 that make a dominant
 * (Bowie, GH #2147); the set is `isDominantSeventhVariant`'s. The parser emits
 * no plain `13`, so it is not listed there. Sus dominants have no third and
 * stay out.
 *
 * Type-predicate form so callers narrow to `ChordVariant` (the branch with
 * `.root`); `Chord` is a union that includes `ChordNotes` which lacks `.root`.
 */
function isDominantSeventh(chord: Chord): chord is ChordVariant {
  return isChordVariant(chord) && isDominantSeventhVariant(chord)
}
